import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "./ui/button";

const CHECK_LABELS = {
  right_to_work_uk: "Right to work in the UK",
  minimum_working_age: "Meets minimum working age (16+)",
  english_communication: "Communicates effectively in English",
  basic_literacy_numeracy_digital: "Basic literacy, numeracy and digital skills",
  prepared_for_identity_reference_checks: "Prepared for identity and reference checks",
  medical_disclosure_honesty: "Committed to honest medical disclosure",
  understands_shift_patterns: "Understands railway shift patterns",
  can_travel_without_public_transport: "Can travel to work without relying on public transport",
  willing_to_complete_training: "Willing to complete required training",
  motivation_for_railway_career: "Motivated to pursue a railway career",
};

const SCORE_LABELS = {
  eligibility_readiness_pct: "Eligibility readiness",
  compliance_readiness_pct: "Compliance readiness",
  operational_readiness_pct: "Operational readiness",
  motivation_readiness_pct: "Motivation readiness",
  overall_stage_one_pct: "Overall Stage 1 score",
};

const HARD_BLOCKERS = new Set([
  "right_to_work_uk",
  "english_communication",
  "understands_shift_patterns",
]);

const TALENT_ROLE_LABELS = {
  "train-driver": "Train driver",
  conductor: "Conductor",
  "depot-driver": "Depot driver",
  "maintenance-rolling-stock": "Maintenance (Rolling Stock)",
  "maintenance-track-infrastructure": "Maintenance (Track or Infrastructure)",
  administrative: "Administrative",
  graduate: "Graduate",
  signalling: "Signalling",
  other: "Other",
};

const STAGE_TWO_OUTCOME_LABELS = {
  pass: "Pass",
  review_required: "Review required",
  not_met: "Not met",
  passed_by_admin_review: "Passed by admin review",
  not_met_admin_confirmed: "Not met (admin confirmed)",
  confirmation_assessment_assigned: "Confirmation assessment assigned",
};

function stageTwoOutcomeLabel(value) {
  const key = String(value || "").toLowerCase();
  return STAGE_TWO_OUTCOME_LABELS[key] || (key ? key.replaceAll("_", " ") : "Not available yet");
}

function stageTwoOutcomeTone(value) {
  const key = String(value || "").toLowerCase();
  if (key.includes("pass")) return "border-lime-300 bg-lime-100 text-lime-900";
  if (key.includes("not_met") || key.includes("decline")) return "border-red-300 bg-red-100 text-red-900";
  if (key.includes("review") || key.includes("confirmation")) return "border-amber/50 bg-amber/20 text-ink";
  return "border-border bg-paper text-ink";
}

function ScoreBar({ pct }) {
  const n = Number.isFinite(pct) ? pct : 0;
  const colour = n >= 80 ? "bg-lime-500" : n >= 50 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 rounded-full bg-border overflow-hidden">
        <div className={`h-2 rounded-full ${colour}`} style={{ width: `${n}%` }} />
      </div>
      <span className="text-sm font-semibold text-ink w-10 text-right">{n}%</span>
    </div>
  );
}

/**
 * CandidateResponsesPanel
 *
 * Props:
 *   member      — serialised talent member object (from API)
 *   isAdmin     — show age and driver-age fields (only meaningful if backend returned them)
 *   open        — dialog open state
 *   onClose     — called when dialog should close
 *   onMessage   — optional: called when employer clicks "Message". If omitted, no message button shown.
 */
export default function CandidateResponsesPanel({ member, isAdmin = false, open, onClose, onMessage }) {
  if (!member) return null;

  const checks = member.gateway_checks || {};
  const scores = member.gateway_scores || {};
  const stageTwo = member.stage_two_profile || {};
  const name = member.contact_name || `${member.first_name || ""} ${member.last_name || ""}`.trim() || "Candidate";
  const roles = (member.target_roles || []).map((r) => TALENT_ROLE_LABELS[r] || r);
  const stageTwoRoleSuitability = (member.role_suitability || []).map((r) => TALENT_ROLE_LABELS[r] || r);
  const allCheckKeys = Object.keys(CHECK_LABELS);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {name}
          </DialogTitle>
          <div className="text-sm text-muted-foreground mt-0.5">{member.email}</div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {roles.map((r) => (
              <span key={r} className="inline-flex items-center rounded-full bg-sky/10 border border-sky/30 px-2 py-0.5 text-[11px] text-ink">
                {r}
              </span>
            ))}
            {member.preferred_postcode && (
              <span className="inline-flex items-center rounded-full bg-paper border border-border px-2 py-0.5 text-[11px] font-mono text-ink">
                {member.preferred_postcode}
              </span>
            )}
            {member.preferred_locations && (
              <span className="inline-flex items-center rounded-full bg-paper border border-border px-2 py-0.5 text-[11px] text-ink">
                {member.preferred_locations}
              </span>
            )}
          </div>
        </DialogHeader>

        {/* Stage 1 answers */}
        <div className="mt-4">
          <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Stage 1 — candidate responses</h4>
          <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
            {allCheckKeys.map((key) => {
              const passed = Boolean(checks[key]);
              const isBlocker = HARD_BLOCKERS.has(key);
              return (
                <div key={key} className="flex items-center gap-3 px-4 py-2.5">
                  {passed ? (
                    <CheckCircle2 className="w-4 h-4 text-lime-600 shrink-0" />
                  ) : (
                    <XCircle className={`w-4 h-4 shrink-0 ${isBlocker ? "text-red-600" : "text-amber-500"}`} />
                  )}
                  <span className="flex-1 text-sm text-ink">{CHECK_LABELS[key]}</span>
                  {isBlocker && !passed && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-red-600 border border-red-300 rounded-full px-2 py-0.5">
                      Hard blocker
                    </span>
                  )}
                  {passed && (
                    <span className="text-[11px] text-lime-700 font-semibold">Yes</span>
                  )}
                  {!passed && (
                    <span className="text-[11px] text-muted-foreground font-semibold">No</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Stage 2 summary */}
        <div className="mt-5">
          <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Stage 2 - operational behaviour summary</h4>
          {!stageTwo.completed_at && !stageTwo.progression_outcome && !stageTwo.combined_profile ? (
            <div className="rounded-lg border border-dashed border-border bg-paper/40 px-4 py-3 text-sm text-muted-foreground">
              No completed Stage 2 result is available for this candidate yet.
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-paper/30 p-3 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${stageTwoOutcomeTone(stageTwo.progression_outcome)}`}>
                  {stageTwoOutcomeLabel(stageTwo.progression_outcome)}
                </span>
                {stageTwo.completed_at && (
                  <span className="text-[11px] text-muted-foreground">Completed {String(stageTwo.completed_at).slice(0, 10)}</span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="rounded-md border border-border bg-background px-3 py-2">
                  <div className="uppercase tracking-wider text-muted-foreground">Overall safety</div>
                  <div className="mt-0.5 text-sm font-semibold text-ink">{stageTwo.overall_safety_pct ?? "-"}%</div>
                </div>
                <div className="rounded-md border border-border bg-background px-3 py-2">
                  <div className="uppercase tracking-wider text-muted-foreground">Behaviour profile</div>
                  <div className="mt-0.5 text-sm font-semibold text-ink">{stageTwo.combined_profile || "-"}</div>
                </div>
                <div className="rounded-md border border-border bg-background px-3 py-2">
                  <div className="uppercase tracking-wider text-muted-foreground">Critical responses</div>
                  <div className="mt-0.5 text-sm font-semibold text-ink">{stageTwo.critical_response_count ?? "-"}</div>
                </div>
                <div className="rounded-md border border-border bg-background px-3 py-2">
                  <div className="uppercase tracking-wider text-muted-foreground">Absolute red-lines</div>
                  <div className="mt-0.5 text-sm font-semibold text-ink">{stageTwo.absolute_red_line_count ?? "-"}</div>
                </div>
              </div>

              <div className="text-xs text-muted-foreground">
                Primary: <span className="text-ink font-semibold">{stageTwo.primary_tendency || "-"}</span>
                {" · "}
                Secondary: <span className="text-ink font-semibold">{stageTwo.secondary_tendency || "-"}</span>
                {" · "}
                Strength: <span className="text-ink font-semibold capitalize">{stageTwo.profile_strength_indicator || "-"}</span>
              </div>

              {stageTwo.outcome_reason_primary && (
                <div className="rounded-md border border-sky/30 bg-sky/10 px-3 py-2 text-xs text-ink">
                  <div className="uppercase tracking-wider text-muted-foreground">Outcome reason</div>
                  <div className="mt-1 leading-relaxed">{stageTwo.outcome_reason_primary}</div>
                </div>
              )}

              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Role suitability</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {stageTwoRoleSuitability.length === 0 ? (
                    <span className="text-xs text-muted-foreground">-</span>
                  ) : stageTwoRoleSuitability.map((r) => (
                    <span key={`fit-${r}`} className="inline-flex items-center rounded-full border border-lime-300 bg-lime-50 px-2 py-0.5 text-[11px] text-lime-900">
                      {r}
                    </span>
                  ))}
                </div>
              </div>

              {Array.isArray(stageTwo.assessment_integrity_flags) && stageTwo.assessment_integrity_flags.length > 0 && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Integrity flags:</span>{" "}
                  {stageTwo.assessment_integrity_flags.map((flag) => (
                    <span key={flag} className="inline-flex items-center rounded-full border border-amber/40 bg-amber/20 px-2 py-0.5 text-[10px] font-semibold text-ink mr-1 mb-1">
                      {flag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Readiness scores */}
        {Object.keys(scores).length > 0 && (
          <div className="mt-5">
            <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Readiness scores</h4>
            <div className="space-y-2.5">
              {Object.entries(SCORE_LABELS).map(([key, label]) =>
                scores[key] !== undefined ? (
                  <div key={key}>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>{label}</span>
                    </div>
                    <ScoreBar pct={scores[key]} />
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}

        {/* Admin-only: age details */}
        {isAdmin && (member.age_years !== undefined || member.driver_age_ready !== undefined) && (
          <div className="mt-5">
            <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Personal details (admin only)</h4>
            <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
              {member.age_years !== undefined && (
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-muted-foreground">Age at registration</span>
                  <span className="font-semibold text-ink">{member.age_years} years</span>
                </div>
              )}
              {member.driver_age_ready !== undefined && (
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-muted-foreground">Train-driver age ready (18+)</span>
                  {member.driver_age_ready ? (
                    <span className="font-semibold text-lime-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Yes</span>
                  ) : (
                    <span className="font-semibold text-amber-700 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> No</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Registration notes */}
        {member.talent_pool_notes && (
          <div className="mt-5">
            <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Registration notes</h4>
            <div className="rounded-lg border border-border bg-paper/50 px-4 py-3 text-sm text-ink whitespace-pre-wrap">
              {member.talent_pool_notes}
            </div>
          </div>
        )}

        {/* Missing requirements summary */}
        {(member.stage_one_missing_requirements || []).length > 0 && (
          <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <h4 className="text-xs uppercase tracking-widest text-red-700 font-semibold mb-2">Missing requirements</h4>
            <ul className="space-y-1">
              {member.stage_one_missing_requirements.map((k) => (
                <li key={k} className="text-sm text-red-800">
                  {CHECK_LABELS[k] || k}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action footer */}
        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap border-t border-border pt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {onMessage && (
            <Button
              className="bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950 font-semibold"
              onClick={() => { onClose(); onMessage(member); }}
            >
              Message {name.split(" ")[0]}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
